import * as queryString from 'query-string';
import API from '../api';
import * as actions from '../constants';
import generateActions from './baseAction';
import paginate from '../../data/paginate';

const {
  showError,
  clearErrors,
  getBooking,
  addBooking,
  updateBooking,
  removeBooking,
} = generateActions('booking', 'bookings');

/**
 * Get the bookings counts for the current days by status
 */
const getBookingCounts = () => (dispatch) => {
  dispatch({ type: actions.REQUEST_BOOKING_COUNT });
  return API.request('/bookings/counts')
    .then((response) => response.json())
    .then((response) => {
      dispatch({ type: actions.RECEIVE_BOOKING_COUNT });
      return response;
    });
};

const getRevision = (bookingId, revisionId) => (dispatch) => {
  return API.request(`/bookings/${bookingId}/revision/${revisionId}`)
    .then((response) => response.json())
    .then((response) => {
      return response;
    });
};

/**
 * Get a list of bookings in a range
 * @param {Date} start
 * @param {Date} end
 * @param {Boolean?} isComplete
 */
const getBookingRange = (start, end, isComplete?, showAll?): any => (
  dispatch,
) => {
  dispatch({ start, end, type: actions.REQUEST_BOOKING_RANGE });
  const query: any = { start, end };
  if (showAll !== undefined) {
    query.show_all = showAll;
  } else if (isComplete !== undefined) {
    query.is_complete = isComplete;
  }
  return API.request(`/bookings/?${queryString.stringify(query)}`)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_RANGE,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_RANGE,
        error: 'There was a problem fetching bookings',
      });
      return false;
    });
};

type BookingStatus =
  | 'PENDING'
  | 'UNASSIGNED'
  | 'READY'
  | 'ACTIVE'
  | 'ON-HOLD'
  | 'COMPLETE';

/**
 * Get a list of bookings with a status
 * @param {String} status
 * @param {Date?} date
 * @param {Date?} endDate
 */
const getBookingsWithStatus = (
  status: BookingStatus | string,
  date?: Date,
  endDate?: Date,
): any => (dispatch) => {
  dispatch({ status, date, type: actions.REQUEST_BOOKING_STATUS });
  let url = `/bookings/status/${status}`;
  if (date !== undefined) {
    url += `?${queryString.stringify({ date })}`;
  } else if (endDate !== undefined) {
    url += `?${queryString.stringify({ endDate })}`;
  }
  return API.request(url)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_STATUS,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_STATUS,
        error: 'There was a problem fetching bookings',
      });
      return false;
    });
};

/**
 * Send a hire agreement to the contact attached to the booking
 * @param {Number} id
 */
const sendHireAgreement = (id) => (dispatch) => {
  dispatch({ type: actions.REQUEST_HIRE_AGREEMENT_EMAIL });
  return API.request(`/bookings/${id}/email-hire-agreement`)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_HIRE_AGREEMENT_EMAIL,
        data: response,
      });
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_HIRE_AGREEMENT_EMAIL,
        error:
          'There was a problem sending the hire agreement to the booking contact',
      });
    });
};

/**
 * Get a list of bookings with a payment status
 * @param {String} status
 * @param {Number} payment_status
 * @param {Number} page
 * @param {String?} order
 * @param {String?} sort
 * @param {Date?} date
 * @param {Number?} limit If 0 is passed pagination will be ignored
 * @param {}
 */
const getBookingsWithPaymentStatus = (
  paymentStatus: 0 | 1 | 2 | 3 | 4,
  page: number = 0,
  status?: BookingStatus,
  order?: string,
  sort?: string,
  date?: Date,
  limit: number = paginate.payments.MAX,
  search?: string,
  companyId?,
): any => (dispatch) => {
  dispatch({ status, date, type: actions.REQUEST_BOOKING_PAYMENT_STATUS });
  let route;
  // If the status is not set only get the bookings with the payment status
  if (status !== undefined) {
    route = `bookings/status/${status}/payment`;
  } else {
    route = `bookings/payment-status`;
  }
  return API.request(
    `/${route}/${paymentStatus}?${queryString.stringify({
      page,
      order,
      sort,
      date,
      limit,
      search,
      company_id: companyId,
    })}`,
  )
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_PAYMENT_STATUS,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_PAYMENT_STATUS,
        error: 'There was a problem fetching bookings',
      });
      return false;
    });
};

/**
 * Get a list of collection times for a booking
 * @param {Date} start
 */
const getBookingCollectionTimes = (start): any => (dispatch) => {
  dispatch({ start, type: actions.REQUEST_BOOKING_COLLECTION_TIMES });
  return API.request(`/bookings/times?${queryString.stringify({ start })}`)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_COLLECTION_TIMES,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_COLLECTION_TIMES,
        error: 'There was a problem fetching booking collection times',
      });
      return false;
    });
};

/**
 * Pay for a booking
 * @param {Number} id
 * @param {{ subtotal, type, company_id, driver_id, description }} data
 */
const payBooking = (
  id: number,
  data: {
    total: number;
    type:
      | 'CASH'
      | 'CARD'
      | 'BACS'
      | 'CHEQUE'
      | 'ACCOUNT'
      | 'PRE-AUTH'
      | 'CREDIT-NOTE';
    company_id?: number;
    driver_id?: number;
    description?: string;
  },
): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_PAY_BOOKING });
  return API.request(`/bookings/${id}/pay`, 'POST', data)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_PAY_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_PAY_BOOKING,
        error: 'There was a problem paying the booking',
      });
      return false;
    });
};

/**
 * Validate if a booking is ready to be assigned
 * @param {Number} id
 */
const validateBooking = (id: number): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_VALIDATE_BOOKING });
  return API.request(`/bookings/${id}/validate`)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_VALIDATE_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_VALIDATE_BOOKING,
        error: 'There was a problem validating the booking',
      });
      return false;
    });
};

/**
 * Get all the vehicles available for a booking
 * @param {Number} id
 */
const getBookingVehicles = (id: number): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_BOOKING_VEHICLES });
  return API.request(`/bookings/${id}/vehicles`)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_VEHICLES,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_VEHICLES,
        error: 'There was a problem requesting the available vehicles',
      });
      return false;
    });
};

/**
 * Approve a pending booking for collection
 * @param {Number} booking_id
 */
const approveBooking = (id: number): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_APPROVE_BOOKING });
  return API.request(`/bookings/${id}/approve`, 'POST')
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_APPROVE_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_APPROVE_BOOKING,
        error: 'There was a problem approving the booking',
      });
      return false;
    });
};

/**
 * Set a booking to the pending state
 * @param {Number} booking_id
 */
const disapproveBooking = (id: number): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_DISAPPROVE_BOOKING });
  return API.request(`/bookings/${id}/disapprove`, 'POST')
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_DISAPPROVE_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_DISAPPROVE_BOOKING,
        error: 'There was a problem setting the booking to pending',
      });
      return false;
    });
};

/**
 * Assign a vehicle to a unassigned booking
 * @param {Number} id
 * @param {{ vehicle_id, mileage }} data
 */
const assignBooking = (
  id: number,
  data: { vehicle_id: number; mileage: number },
): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_ASSIGN_BOOKING });
  return API.request(`/bookings/${id}/assign`, 'POST', data)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_ASSIGN_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_ASSIGN_BOOKING,
        error: 'There was a problem assigning a vehicle to the booking',
      });
      return false;
    });
};

/**
 * Unassign a vehicle from a ready booking
 * @param {Number} id
 */
const unassignBooking = (id: number): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_UNASSIGN_BOOKING });
  return API.request(`/bookings/${id}/unassign`, 'POST')
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_UNASSIGN_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_UNASSIGN_BOOKING,
        error: 'There was a problem unassigning a vehicle from the booking',
      });
      return false;
    });
};

/**
 * Dispatch a ready booking
 * @param {Number} id
 */
const dispatchBooking = (id: number): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_DISPATCH_BOOKING });
  return API.request(`/bookings/${id}/dispatch`, 'POST')
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_DISPATCH_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_DISPATCH_BOOKING,
        error: 'There was a problem dispatching the booking',
      });
      return false;
    });
};

interface BookingReturnData {
  mileage_after: number;
}

/**
 * Return an active booking
 * @param {Number} id
 */
const returnBooking = (id: number, data: BookingReturnData): any => (
  dispatch,
) => {
  dispatch({ id, type: actions.REQUEST_RETURN_BOOKING });
  return API.request(`/bookings/${id}/return`, 'POST', data)
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_RETURN_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_RETURN_BOOKING,
        error: 'There was a problem returning the booking',
      });
      return false;
    });
};

/**
 * Resolve a booking
 * @param {Number} id
 */
const resolveBooking = (id: number): any => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_RESOLVE_BOOKING });
  return API.request(`/bookings/${id}/resolve`, 'POST')
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_RESOLVE_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_RESOLVE_BOOKING,
        error: 'There was a problem resolving the booking',
      });
      return false;
    });
};

/**
 * Cancel a booking
 * @param {Number} id
 */
const cancelBooking = (id: number) => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_CANCEL_BOOKING });
  return API.request(`/bookings/${id}/cancel`, 'POST')
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_CANCEL_BOOKING,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_CANCEL_BOOKING,
        error: 'There was a problem cancelling the booking',
      });
      return false;
    });
};

/**
 * Re-assign a booking's provisional vehicle
 * @param {Number} id
 * @param {Number} provisionalVehicle
 */
const setBookingProvisionalVehicle = (
  id: number,
  provisionalVehicle: number,
) => (dispatch) => {
  dispatch({ id, type: actions.REQUEST_BOOKING_SET_PROVISIONAL_VEHICLE });
  return API.request(`/bookings/${id}/provisional`, 'POST', {
    provisional_vehicle_id: provisionalVehicle,
  })
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_SET_PROVISIONAL_VEHICLE,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_SET_PROVISIONAL_VEHICLE,
        error: "There was a problem setting the booking's provisional vehicle",
      });
      return false;
    });
};

/**
 * Find a booking given the invoice id
 */
const getInvoices = (page: number = 0, search: string = '') => (dispatch) => {
  dispatch({ search, type: actions.REQUEST_BOOKING_INVOICE });
  return API.request(
    `/bookings/invoices?page=${page}&limit=${
      paginate.bookingRelations.MAX
    }&search=${search}`,
  )
    .then((response) => response.json())
    .then((response) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_INVOICE,
        data: response,
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        type: actions.RECEIVE_BOOKING_INVOICE,
        error: 'There was a problem getting the bookings invoices',
      });
      return false;
    });
};

export {
  addBooking,
  approveBooking,
  assignBooking,
  cancelBooking,
  clearErrors,
  disapproveBooking,
  dispatchBooking,
  getBooking,
  getBookingCollectionTimes,
  getBookingCounts,
  getBookingRange,
  getBookingsWithPaymentStatus,
  getBookingsWithStatus,
  getBookingVehicles,
  payBooking,
  removeBooking,
  resolveBooking,
  returnBooking,
  sendHireAgreement,
  showError,
  unassignBooking,
  updateBooking,
  validateBooking,
  setBookingProvisionalVehicle,
  getRevision,
  getInvoices,
};
